/* eslint-disable no-restricted-imports */
import { FC, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useOpenDialog } from '../../../routing/ModalRoute';
import { DIALOGS } from '../../../routing/constants';
import { useCurrentUserIsLoading } from '../../../routing/users/useCurrentUser';
import { isBlogger, isDeclinedAdmin } from '../../../utils/accessService';
import { getWindow } from '../../../utils/getWindow';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useProfileSticker } from '../../hooks/useProfileSticker';
import { IconAdd } from '../../icon/IconAdd';
import { IconBurger } from '../../icon/IconBurger';
import { IconDiscovery } from '../../icon/IconDiscovery';
import { IconDiscoveryFilled } from '../../icon/IconDiscoveryFilled';
import { IconSearch } from '../../icon/IconSearch';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { Avatar } from '../../media/Avatar';
import { Sticker } from '../../styles';
import { CpaPromoBalance } from '../../users/CpaPromoBalance/CpaPromoBalance';
import { MOBILE_FOOTER_HEIGHT } from '../DashboardLayout';
import { ControlContainer } from './styles';
import { MobileTabbarProps } from './types';

export const MobileTabbar: FC<MobileTabbarProps> = ({
    user,
    cpaBalance,
    openMultiProfileDialog,
}) => {
    const profile = user?.profile;
    const isAdmin = profile && !isDeclinedAdmin(user?.role);
    const openDialog = useOpenDialog();
    const isCreateCollectionEnabled =
        String(useExperimentFlag('create_collection_from_plus_enabled')) === '1';
    const isUserLoading = useCurrentUserIsLoading();

    const { isVisible: isProfileStickerVisible } = useProfileSticker(user);

    const openCreateCollectionDialog = useCallback(() => {
        openDialog(DIALOGS.CREATE_COLLECTION);
    }, [openDialog]);

    if (isUserLoading) {
        return null;
    }

    return (
        <Flex
            justifyContent="space-between"
            alignItems="center"
            height={MOBILE_FOOTER_HEIGHT}
            direction={{ sm: 'column' }}
            width="100%"
            py={1}
        >
            <ControlContainer as={NavLink} to="/">
                {({ isActive }) => (
                    <Flex fontSize={24} dataTestId={DataTestId.DISCOVERY_BUTTON}>
                        {isActive ? <IconDiscoveryFilled /> : <IconDiscovery />}
                    </Flex>
                )}
            </ControlContainer>
            {isAdmin ? (
                <ControlContainer as={NavLink} to="/posts/create">
                    <Flex fontSize={30} dataTestId={DataTestId.DISCOVERY_BUTTON}>
                        <IconAdd />
                    </Flex>
                </ControlContainer>
            ) : (
                isCreateCollectionEnabled &&
                !isUserLoading && (
                    <ControlContainer onClick={openCreateCollectionDialog}>
                        <Flex fontSize={30} dataTestId={DataTestId.DISCOVERY_BUTTON}>
                            <IconAdd />
                        </Flex>
                    </ControlContainer>
                )
            )}
            <ControlContainer
                as={NavLink}
                to={`/search?searchType=post&query=${
                    getWindow()?.sessionStorage.getItem('lastSearch') ?? ''
                }`}
            >
                {({ isActive }) => (
                    <Flex fontSize={24} dataTestId={DataTestId.SEARCH}>
                        {isActive ? (
                            <IconSearch stroke="currentcolor" />
                        ) : (
                            <IconSearch stroke="none" />
                        )}
                    </Flex>
                )}
            </ControlContainer>
            {profile && !isBlogger(user?.role) ? (
                <ControlContainer onClick={() => openMultiProfileDialog({ user })}>
                    <Flex fontSize={24}>
                        <IconBurger stroke="currentcolor" />
                    </Flex>
                </ControlContainer>
            ) : (
                <ControlContainer
                    as={NavLink}
                    to={
                        isBlogger(user?.role)
                            ? `/businesses/${user?.profile?.publicId}`
                            : '/profile'
                    }
                >
                    {({ isActive }) => (
                        <Flex fontSize={24} dataTestId={DataTestId.PROFILE} position="relative">
                            <Avatar
                                src={
                                    isBlogger(user?.role)
                                        ? user?.profile?.avatarUrl
                                        : user?.avatarUrl
                                }
                                title={
                                    isBlogger(user?.role)
                                        ? user?.profile?.displayName
                                        : user?.displayName
                                }
                                size={24}
                                border={isActive ? '2px solid var(--color-primary)' : ''}
                            />
                            {isProfileStickerVisible && !cpaBalance && !isActive ? (
                                <Sticker />
                            ) : null}
                            {cpaBalance ? (
                                <Flex
                                    position="absolute"
                                    bottom="-8px"
                                    left="-16px"
                                    width="56px"
                                    direction="row"
                                    justifyContent="center"
                                >
                                    <CpaPromoBalance balance={cpaBalance} maxWidth="100%" isShort />
                                </Flex>
                            ) : null}
                        </Flex>
                    )}
                </ControlContainer>
            )}
        </Flex>
    );
};
